import { window } from "global";
import React, { useEffect, useMemo } from "react";
import { useGlobal } from "reactn";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { REGION_OPTIONS } from "./leaderboard-filters";
import { Helmet } from "react-helmet-async";
import MediaQuery from "components/MediaQuery";
import LeaderboardHeader from "./components/LeaderboardHeader";
import LeaderboardNav from "./components/LeaderboardNav";
import LeaderboardRegionFilter from "./components/filters/LeaderboardRegionFilter";
import LeaderboardSummonerInput from "./components/filters/LeaderboardSummonerInput";
import LeaderboardTableContainer from "./components/LeaderboardTableContainer";
import { getNotFoundImg } from "@outplayed/riot-assets";
import { ReactComponent as FilterIcon } from "svg/filters-icon.svg";
import { useLeaderboardPage } from "@ugg/shared/api/requests/leaderboards/leaderboard-page";
import { usePlayerOverallRanking } from "@ugg/shared/api/requests/leaderboards/ladder-ranking";
import { splitRiotId } from "@ugg/shared/utils/riot-id-helpers";

const FiltersRow = (props) => {
  const queryParams = useQueryString();
  const { region } = queryParams;

  return (
    <div className="leaderboard-ranking_filters-row">
      <div className="leaderboard_filters">
        <div className="filter-label">
          <FilterIcon />
          <MediaQuery min="TABLET" max="DESKTOP_LARGE">
            <span>Filters</span>
          </MediaQuery>
        </div>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <LeaderboardRegionFilter value={region} />
        </MediaQuery>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE">
          <LeaderboardRegionFilter value={region} isMobile />
        </MediaQuery>
      </div>
      <LeaderboardSummonerInput />
    </div>
  );
};

const LeaderboardRanking = (props) => {
  const history = useHistory();
  const location = useLocation();
  const [globalRegion] = useGlobal("clientRegion");
  const queryParams = useQueryString();
  const { region, user, page } = queryParams;
  const [userRiorUserName, userRiotTagLine] = splitRiotId(user);

  useEffect(() => {
    if (!region || !REGION_OPTIONS.find((option) => option.value === region.toLowerCase())) {
      history.replace({ pathname: location.pathname, search: `region=${globalRegion}` });
    }
  }, [region]);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [JSON.stringify(queryParams)]);

  const selectedRegion = useMemo(
    () => REGION_OPTIONS.find((option) => option.value === (region && region.toLowerCase())),
    [region],
  );
  const { fullLabel } = selectedRegion || {};

  const {
    loading: loadingOverallRanking,
    data: overallRankingData,
    error: errorOverallRanking,
  } = usePlayerOverallRanking(userRiorUserName, userRiotTagLine, region, 420, {
    ssr: false,
    skip: !userRiorUserName || !userRiotTagLine,
  });

  const { overallRanking } = overallRankingData || {};
  const { overallRanking: overallRankingNum } = overallRanking || {};
  const summonerPageNum = Math.floor((overallRankingNum - 1) / 100) + 1;
  const leaderboardPageNum = !isNaN(summonerPageNum) ? summonerPageNum : !isNaN(parseInt(page)) ? parseInt(page) : 1;

  const {
    loading: loadingLeaderboardPage,
    data: leaderboardPageData,
    error: errorLeaderboardPage,
  } = useLeaderboardPage(region, 420, leaderboardPageNum, {
    ssr: false,
    skip: loadingOverallRanking,
  });

  const { leaderboardPage } = leaderboardPageData || {};
  const { players } = leaderboardPage || {};
  const isLoading = loadingOverallRanking || loadingLeaderboardPage;
  let content = null;

  if (isLoading) {
    content = (
      <div className="content-section leadboard_table_loading">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  } else if (!players || players.length === 0) {
    content = (
      <div className="content-section leaderboard_table_error">
        <img src={getNotFoundImg()} alt="No data found" />
        <p>Sorry, there is no data available. Please try changing the filters or page.</p>
      </div>
    );
  } else {
    content = (
      <LeaderboardTableContainer page={leaderboardPageNum} data={leaderboardPageData && leaderboardPageData.leaderboardPage} />
    );
  }

  return (
    <div className="leaderboard-ranking">
      <Helmet>
        <title>LoL Leaderboards - Best Ranking Challenger Players All Regions</title>
        <meta
          name="description"
          content="U.GG’s player leaderboards track the highest ranked League of Legends players across every region. Player profiles include best champions and LP per game."
        />
      </Helmet>
      <LeaderboardHeader mainTitle={"Leaderboards"} subTitle={fullLabel || ""} />
      {/*<LeaderboardNav />*/}
      <FiltersRow />
      {content}
    </div>
  );
};

export default LeaderboardRanking;
